import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { expenseCategoriesApi } from './expenseCategoriesApi';
import { expensesApi } from './expensesApi';
import { financeApi } from './financeApi';
import type {
  ExpenseCategoryListParams,
  ExpenseCategoryPayload,
  ExpenseListParams,
  ExpensePayload,
  FinanceRange,
} from './types';

const KEY = 'expenses';
const CATEGORIES_KEY = 'expenseCategories';
const SUMMARY_KEY = 'financeSummary';

/**
 * An expense write moves three caches: the expense book, the range summary
 * that nets it against takings, and the categories — which carry how many
 * expenses each one holds.
 */
const useInvalidateExpenses = () => {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [KEY] });
    void qc.invalidateQueries({ queryKey: [CATEGORIES_KEY] });
    void qc.invalidateQueries({ queryKey: [SUMMARY_KEY] });
  };
};

export const useExpenses = (params: ExpenseListParams) =>
  useQuery({
    queryKey: [KEY, 'list', params],
    queryFn: () => expensesApi.list(params),
    // Same call the till and the calendar make: stepping through date ranges is
    // the main interaction, and a spinner on every step is worse than a stale
    // table for a moment.
    placeholderData: (previous) => previous,
  });

export const useCreateExpense = () => {
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: (data: ExpensePayload) => expensesApi.create(data),
    onSuccess: invalidate,
  });
};

export const useUpdateExpense = () => {
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ExpensePayload> }) =>
      expensesApi.update(id, data),
    onSuccess: invalidate,
  });
};

export const useDeleteExpense = () => {
  const invalidate = useInvalidateExpenses();
  return useMutation({
    mutationFn: (id: number) => expensesApi.remove(id),
    onSuccess: invalidate,
  });
};

/** Every category, active and archived; callers filter. A salon has a handful. */
export const useExpenseCategories = (params: ExpenseCategoryListParams = { limit: 100 }) =>
  useQuery({
    queryKey: [CATEGORIES_KEY, params],
    queryFn: () => expenseCategoriesApi.list(params),
  });

/**
 * Renaming a category renames it on every expense row too, so the expense book
 * is invalidated along with the categories.
 */
const useInvalidateCategories = () => {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: [CATEGORIES_KEY] });
    void qc.invalidateQueries({ queryKey: [KEY] });
  };
};

export const useCreateExpenseCategory = () => {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: (data: ExpenseCategoryPayload) => expenseCategoriesApi.create(data),
    onSuccess: invalidate,
  });
};

export const useUpdateExpenseCategory = () => {
  const invalidate = useInvalidateCategories();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ExpenseCategoryPayload }) =>
      expenseCategoriesApi.update(id, data),
    onSuccess: invalidate,
  });
};

export const useFinanceSummary = (range: FinanceRange) =>
  useQuery({
    queryKey: [SUMMARY_KEY, range],
    queryFn: () => financeApi.summary(range),
    placeholderData: (previous) => previous,
  });
